import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@material-ui/core';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Product } from '@setvi/shared/interfaces';
import { SButton } from '@setvi/shared/components';
import { CheckIconV2 } from '@setvi/shared/components/sicons';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';

import { Views } from 'Styles';
import { getRfqMatchedItemsQuery } from 'Services';
import { Grid as GridIcon, List as ListIcon } from 'Components/Shared/Icons';
import { AIProductsFilters, ProductsList } from 'pages/products/components';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import {
  filterByCost,
  filterByStock,
  filterByManufacturer
} from 'pages/products/hooks/filter-functions';
import {
  ProductFilterInterface,
  ProductFilterTypes
} from 'pages/products/hooks/use-ai-search-result';
import { useRFQStore } from 'pages/auto-rfq/store';

import { Footer } from '../catalog/styled';

interface ExpandedProducts extends Product {
  order?: number;
}

export interface ProductMatchProps {
  ids?: string[];
  fetch?: boolean;
  products: Product[];
  selected: Product;
  setSelected: (product: Product) => void;
  onClose?: () => void;
}

const ProductMatch = ({
  ids,
  fetch,
  products,
  selected,
  onClose,
  setSelected
}: ProductMatchProps) => {
  const { id } = useParams();

  const { captureAnalytics } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exported, setExported] = useState(selected || null);
  const {
    updateAnalytic,
    analytics,
    privateLabelsSelected,
    updatePrivateLabelsSelected
  } = useRFQStore();

  const [selectedFilters, setSelectedFilters] =
    useState<ProductFilterInterface>({
      [ProductFilterTypes.Manufacturer]: [],
      [ProductFilterTypes.Stock]: null,
      [ProductFilterTypes.Cost]: []
    });
  const { data, isFetching } = useQuery({
    ...getRfqMatchedItemsQuery(ids),
    enabled: !!ids?.length && fetch
  });

  const apiProducts = useMemo(() => {
    if (!fetch) return products;
    if (isFetching) return [];
    const currProducts = data
      ?.map((d: any) => {
        const curr = products.find(
          (p: any) => p.id === d._id || p._id === d._id
        );
        return { ...curr, ...d, external: false };
      })
      .sort((a: ExpandedProducts, b: ExpandedProducts) =>
        a.order >= 0 ? a.order - b.order : b.percentage - a.percentage
      );

    return currProducts || [];
  }, [data, fetch, products, isFetching]);

  const { configuration } = useCatalogConfig();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showStockFilter = configuration?.product?.grid?.stock?.show;
  const showPriceFilter = !!configuration?.product?.grid?.pricing?.length;

  useEffect(() => {
    const currProduct = fetch ? apiProducts : products;

    if (!currProduct?.length || !showPriceFilter) return;

    const minCost = Math.floor(
      currProduct.reduce(
        (min: number, { cost }: { cost: number }) => (cost < min ? cost : min),
        currProduct?.[0]?.cost || 0
      )
    );
    const maxCost = Math.ceil(
      currProduct.reduce(
        (max: number, { cost }: { cost: number }) => (cost > max ? cost : max),
        currProduct?.[0]?.cost || 0
      )
    );

    setSelectedFilters(prev => ({
      ...prev,
      [ProductFilterTypes.Cost]: [minCost || 0, maxCost || 0]
    }));
  }, [fetch, apiProducts, products, showPriceFilter]);

  const aiFilteredProducts = useMemo(() => {
    const currItems = fetch ? apiProducts : products;

    return currItems.filter((i: Product) =>
      [
        filterByManufacturer(selectedFilters.manufacturer, i),
        ...(showStockFilter ? [filterByStock(selectedFilters.stock, i)] : []),
        filterByCost(selectedFilters.cost, i)
      ].every(Boolean)
    );
  }, [apiProducts, selectedFilters, showStockFilter, fetch, products]);

  const handleAnalytics = () => {
    const sourceIsPL = selected?.isPrivateLabel || selected?.privateLabel;
    const targetIsPL = exported?.isPrivateLabel || exported?.privateLabel;

    const analyticsBody = {
      action: 'change',
      id: exported?._id,
      rfqId: id || '',
      sourceProductId: selected?.sku || '',
      targetProductId: exported?.sku || '',
      sourceIsPL,
      targetIsPL
    };

    if (!sourceIsPL && targetIsPL) {
      captureAnalytics(
        {
          subAction: 'Private label SWAP',
          ...analyticsBody

          // !we will add this on auto safe
          // rfqId: '3f7a5102-53c4-46b4-aed2-e123b0b1967a',
        },
        AnalyticsModules.AutoRFQItem
      );

      if (!privateLabelsSelected.includes(exported?.sku)) {
        updatePrivateLabelsSelected([exported?.sku]);
        updateAnalytic({
          privateLabelSwapped: analytics.privateLabelSwapped + 1
        });
      }

      return;
    }

    captureAnalytics(analyticsBody, AnalyticsModules.AutoRFQItem);
  };

  return (
    <Box
      flex={1}
      width="100%"
      height="100%"
      flexDirection="column"
      display="flex"
      justifyContent="space-between">
      <Box
        style={{
          padding: '0px 30px ',
          flex: 1,
          marginTop: 12,
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
          gap: 12,
          justifyContent: 'space-between'
        }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}>
          <AIProductsFilters
            products={fetch ? apiProducts : products}
            showStockFilter={showStockFilter}
            selectedFilters={selectedFilters}
            showPriceFilter={showPriceFilter}
            loading={isFetching}
            onChange={setSelectedFilters}
          />
          <IconButton
            color="inherit"
            style={{
              border: `1px solid #AFBAC6`,
              padding: 10,
              borderRadius: 12
            }}
            disabled={isFetching}
            onClick={() => {
              searchParams.set(
                'view',
                searchParams.get('view') === Views.GRID_VIEW
                  ? Views.LIST_VIEW
                  : Views.GRID_VIEW
              );
              setSearchParams(searchParams);
            }}>
            {searchParams.get('view') !== Views.GRID_VIEW ? (
              <GridIcon fontSize="small" />
            ) : (
              <ListIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        <ProductsList
          products={aiFilteredProducts ?? []}
          config={{
            columns: configuration.product?.columns,
            grid: configuration.product?.grid,
            priceType: configuration?.price,
            stock: configuration?.product?.grid?.stock,
            manufacturerSku: configuration?.product?.grid?.manufacturerSku
          }}
          disableSelect={false}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          fetchMore={() => {}}
          isLoading={fetch && isFetching}
          simpleLoading
          selectedProducts={[exported]}
          handleProductSelect={(isChecked, _products) => {
            if (isChecked) setExported(_products[0]);
          }}
          openProductDetails={() => {}}
          productPricesLoading={false}
          aiSearchActive
          type="radio"
        />
      </Box>

      <Footer>
        <SButton
          color="default"
          variant="text"
          onClick={onClose}
          style={{
            borderRadius: 36,
            color: '#5D7083'
          }}>
          Cancel
        </SButton>

        <SButton
          disabled={!exported}
          tooltipTitle={!exported ? 'Please select item' : ''}
          onClick={() => {
            handleAnalytics();
            setSelected(exported);
          }}
          endIcon={
            <CheckIconV2
              style={{
                width: 20,
                height: 20,
                marginBottom: 2
              }}
            />
          }
          style={{
            borderRadius: 36
          }}>
          Select Item
        </SButton>
      </Footer>
    </Box>
  );
};

export default ProductMatch;
